/**
 * Elite Batch 23 Part 12 — HR continued + Finance/RevOps Start (184-200)
 *
 * DESIGN RULES:
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers have EMPTY inputFields — an AI extraction node follows
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Node names must be descriptive (e.g., "Greenhouse: Trace source attribution", not "API Call")
 * 7. Each workflow needs 8 nodes with proper edges
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{spreadsheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 23 PART 12 — HR continued + Finance/RevOps Start (184-200)
// =============================================================================

export const ELITE_BATCH23_PART12: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 184. RECRUITING SOURCE ATTRIBUTION — Pattern K (Lead Scoring/Routing)
  // ===================================================================
  "Recruiting Source Attribution": {
    nodes: [
      trigger("Greenhouse: Hire Confirmed", "webhook_app", {
        source: "Greenhouse",
        event: "candidate.hired",
        howItStarts:
          "Fires when a candidate is marked as hired in Greenhouse, triggering attribution analysis to trace the recruiting source",
        setup:
          "In Greenhouse > Configure > Dev Center > Webhooks, create a webhook for 'Candidate Hired'. Include candidate_id, application_id, source_id, job_title, and hired_date in payload.",
        inputFields: [],
      }),

      n(
        "2",
        "runCode",
        "Trace source attribution from application to hire",
        280,
        {
          codeDetails: {
            code: "const source = '{source_name}'; const channel = '{source_channel}'; const cost = Number('{source_cost}') || 0; const daysToHire = Number('{days_to_hire}') || 30; return { attribution_source: source, attribution_channel: channel, cost_per_hire: cost, time_to_hire: daysToHire, is_high_cost: cost > 5000 };",
            variables: [
              {
                name: "attribution_source",
                customName: "attribution_source",
                type: "string",
              },
              {
                name: "attribution_channel",
                customName: "attribution_channel",
                type: "string",
              },
              {
                name: "cost_per_hire",
                customName: "cost_per_hire",
                type: "number",
              },
              {
                name: "time_to_hire",
                customName: "time_to_hire",
                type: "number",
              },
              {
                name: "is_high_cost",
                customName: "is_high_cost",
                type: "boolean",
              },
            ],
          },
        },
      ),

      aiNode(
        "3",
        "OpenAI: Validate attribution chain and identify insights",
        520,
        "You are a recruiting analytics specialist. Validate the attribution chain from source to hire and identify any insights about this hiring source's effectiveness compared to benchmarks.",
        "Validate this hire attribution: source={attribution_source}, channel={attribution_channel}, cost=${cost_per_hire}, time_to_hire={time_to_hire} days, job={{{1.body}}}. Return JSON with: validated_source, source_quality (high/medium/low), cost_efficiency (above_avg/avg/below_avg), attribution_confidence (0-100), insight (one sentence about this source).",
        [
          { field: "validated_source", as: "validated_source", type: "string" },
          { field: "source_quality", as: "source_quality", type: "string" },
          { field: "cost_efficiency", as: "cost_efficiency", type: "string" },
          {
            field: "attribution_confidence",
            as: "attribution_confidence",
            type: "number",
          },
          { field: "insight", as: "source_insight", type: "string" },
        ],
      ),

      n(
        "4",
        "hitApi",
        "HubSpot: Update recruiting source data on contact",
        760,
        {
          hitApiDetails: {
            method: "PATCH",
            url: "https://api.hubapi.com/crm/v3/objects/contacts/{hubspot_contact_id}",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"recruiting_source":"{validated_source}","source_channel":"{attribution_channel}","hire_date":"{hired_date}","cost_per_hire":"{cost_per_hire}"}',
              },
            ],
            variables: [
              { name: "id", customName: "updated_contact_id", type: "string" },
              {
                name: "properties.recruiting_source",
                customName: "stored_source",
                type: "string",
              },
            ],
          },
        },
      ),

      sheetsNode(
        "5",
        "Google Sheets: Append hire attribution data",
        1000,
        "RecruitingAttribution",
        '[["{candidate_name}","{job_title}","{validated_source}","{attribution_channel}","{cost_per_hire}","{time_to_hire}","{source_quality}","{attribution_confidence}"]]',
      ),

      n("6", "condition", "Is this a high-cost recruiting source?", 1240, {
        conditionDetails: {
          conditions: [
            { field: "is_high_cost", operator: "equals", value: "true" },
          ],
        },
      }),

      slackNode(
        "7",
        "Slack: Report high-cost source to recruiting ops",
        1480,
        "RECRUITING SOURCE ALERT\\n*New Hire:* {candidate_name} for {job_title}\\n*Source:* {validated_source} ({attribution_channel})\\n*Cost per Hire:* ${cost_per_hire} (HIGH COST)\\n*Time to Hire:* {time_to_hire} days\\n*Quality:* {source_quality} | *Efficiency:* {cost_efficiency}\\n*Insight:* {source_insight}\\n\\nConsider reviewing this source's ROI.",
      ),

      emailNode(
        "8",
        "Email: Send attribution report to recruiting ops",
        1720,
        "{recruiting_ops_email}",
        "Hire Attribution Report: {candidate_name} — {job_title}",
        "A new hire has been attributed and logged.\n\nCandidate: {candidate_name}\nRole: {job_title}\nSource: {validated_source} ({attribution_channel})\nCost per Hire: ${cost_per_hire}\nTime to Hire: {time_to_hire} days\nSource Quality: {source_quality}\nCost Efficiency: {cost_efficiency}\nConfidence: {attribution_confidence}%\n\nInsight: {source_insight}\n\nThis data has been logged to the attribution dashboard for quarterly source analysis.\n\nBest,\nRecruiting Analytics",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
    summary:
      "When a hire is confirmed in Greenhouse, traces the full attribution chain from source to hire, validates with AI, updates HubSpot contact records, and logs to Sheets. High-cost sources are flagged via Slack to recruiting ops for ROI review. All hires receive an attribution report email for quarterly analysis.",
  },

  // ===================================================================
  // 185. CANDIDATE NURTURE SEQUENCE — Pattern G (Re-engagement/Winback)
  // ===================================================================
  "Candidate Nurture Sequence": {
    nodes: [
      trigger("Greenhouse: Silver Medalist Identified", "webhook_app", {
        source: "Greenhouse",
        event: "candidate.rejected",
        howItStarts:
          "Fires when a strong candidate is rejected (silver medalist) in Greenhouse, triggering enrollment in the talent nurture sequence",
        setup:
          "In Greenhouse > Configure > Dev Center > Webhooks, create a webhook for 'Candidate Rejected' with rejection_reason filter for 'Position Filled' or 'Silver Medalist' tags. Include candidate_id, email, name, and applied_job in payload.",
        inputFields: [],
      }),

      n("2", "runCode", "Add candidate to talent pool with nurture tags", 280, {
        codeDetails: {
          code: "const name = '{candidate_name}'; const job = '{applied_job}'; const skills = '{candidate_skills}'; return { pool_tag: 'silver_medalist', nurture_status: 'enrolled', original_role: job, candidate_display: name + ' (prev: ' + job + ')', enrollment_date: new Date().toISOString().slice(0, 10) };",
          variables: [
            { name: "pool_tag", customName: "pool_tag", type: "string" },
            {
              name: "nurture_status",
              customName: "nurture_status",
              type: "string",
            },
            {
              name: "original_role",
              customName: "original_role",
              type: "string",
            },
            {
              name: "candidate_display",
              customName: "candidate_display",
              type: "string",
            },
            {
              name: "enrollment_date",
              customName: "enrollment_date",
              type: "string",
            },
          ],
        },
      }),

      emailNode(
        "3",
        "Email: Send nurture message #1 — company culture and values",
        520,
        "{candidate_email}",
        "We were impressed, {candidate_name} — Let's stay connected",
        "Dear {candidate_name},\n\nThank you for your interest in the {original_role} position and for the time you invested in our interview process. While we moved forward with another candidate for this specific role, we were genuinely impressed by your skills and experience.\n\nWe would love to stay connected for future opportunities that may be an even better fit. Here is a bit about what makes our company special:\n\n- Our mission and values\n- Recent company milestones\n- What our team members say about working here\n\nWe will keep you in mind as new positions open up.\n\nBest regards,\nThe Recruiting Team",
      ),

      n("4", "delayAction", "Wait 30 days before second nurture", 760, {
        delayDetails: { duration: 30, unit: "days" },
      }),

      emailNode(
        "5",
        "Email: Send nurture message #2 — team highlights and growth",
        1000,
        "{candidate_email}",
        "What's new at {company_name}, {candidate_name}",
        "Hi {candidate_name},\n\nIt has been about a month since we last connected, and a lot has been happening here!\n\nHere are some highlights:\n- New product launches and achievements\n- Team growth and culture updates\n- Industry insights we think you will find valuable\n\nWe continue to grow and are always looking for talented people like you. If you are open to exploring opportunities, we would love to hear from you.\n\nBest,\nThe Recruiting Team",
      ),

      n("6", "delayAction", "Wait 60 days before role matching", 1240, {
        delayDetails: { duration: 60, unit: "days" },
      }),

      n("7", "condition", "Is there a new matching role open?", 1480, {
        conditionDetails: {
          conditions: [
            {
              field: "matching_role_count",
              operator: "greaterThan",
              value: "0",
            },
          ],
        },
      }),

      emailNode(
        "8",
        "Email: Send relevant new opportunity to candidate",
        1720,
        "{candidate_email}",
        "A new opportunity that matches your profile, {candidate_name}",
        "Hi {candidate_name},\n\nWe have a new opening that we think aligns well with your background:\n\n{matching_role_title}\n\nBased on your experience with {original_role}, we believe you could be a strong fit for this position. The role offers [key highlights] and aligns with the skills you demonstrated during your previous interview.\n\nInterested? You can apply directly here — your previous interview feedback will be carried over, so the process will be streamlined.\n\nWe would love the chance to work together.\n\nBest,\nThe Recruiting Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8", "output_1"),
    ],
    summary:
      "Enrolls silver-medalist candidates from Greenhouse into a multi-touch nurture sequence. Sends an initial culture-focused email, waits 30 days for a company update email, then waits 60 more days before checking for matching open roles. If a match exists, sends a personalized opportunity email with streamlined application process.",
  },

  // ===================================================================
  // 186. INTERVIEW FEEDBACK COLLECTION — Pattern F (Renewal)
  // ===================================================================
  "Interview Feedback Collection": {
    nodes: [
      trigger("Greenhouse: Interview Completed", "webhook_app", {
        source: "Greenhouse",
        event: "interview.completed",
        howItStarts:
          "Fires when an interviewer marks an interview as completed in Greenhouse, triggering the feedback collection workflow",
        setup:
          "In Greenhouse > Configure > Dev Center > Webhooks, create a webhook for 'Interview Completed'. Include interview_id, interviewer_email, candidate_name, job_title, and interview_type in payload.",
        inputFields: [],
      }),

      emailNode(
        "2",
        "Email: Send scorecard reminder to interviewer",
        280,
        "{interviewer_email}",
        "Please submit your scorecard for {candidate_name} — {job_title}",
        "Hi,\n\nThank you for conducting the {interview_type} interview with {candidate_name} for the {job_title} role.\n\nPlease submit your scorecard in Greenhouse within 24 hours while the conversation is fresh. Your feedback is essential for making timely hiring decisions.\n\nKey areas to evaluate:\n- Technical/functional skills\n- Cultural fit and values alignment\n- Communication and collaboration\n- Overall recommendation (Strong Yes / Yes / No / Strong No)\n\nSubmit your scorecard here: {greenhouse_scorecard_url}\n\nThank you,\nThe Recruiting Team",
      ),

      n("3", "delayAction", "Wait 24 hours for scorecard submission", 520, {
        delayDetails: { duration: 24, unit: "hours" },
      }),

      n(
        "4",
        "condition",
        "Has the interviewer submitted their scorecard?",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                field: "scorecard_submitted",
                operator: "equals",
                value: "true",
              },
            ],
          },
        },
      ),

      slackNode(
        "5",
        "Slack: Nudge interviewer about missing scorecard",
        1000,
        "SCORECARD REMINDER\\n*Interviewer:* {interviewer_name}\\n*Candidate:* {candidate_name}\\n*Role:* {job_title}\\n*Interview Type:* {interview_type}\\n\\nYour scorecard is overdue (24h since interview). Please submit in Greenhouse to avoid delaying the hiring decision.",
      ),

      n(
        "6",
        "hitApi",
        "Greenhouse: Compile all interview feedback for candidate",
        1240,
        {
          hitApiDetails: {
            method: "GET",
            url: "https://harvest.greenhouse.io/v1/applications/{application_id}/scorecards",
            headers: [
              { key: "Authorization", value: "Basic {greenhouse_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            variables: [
              {
                name: "length",
                customName: "scorecards_submitted",
                type: "number",
              },
              {
                name: "[0].overall_recommendation",
                customName: "latest_recommendation",
                type: "string",
              },
              {
                name: "[0].submitted_by.name",
                customName: "submitted_by",
                type: "string",
              },
            ],
          },
        },
      ),

      slackNode(
        "7",
        "Slack: Notify recruiter that feedback is complete",
        1480,
        "INTERVIEW FEEDBACK UPDATE\\n*Candidate:* {candidate_name} for {job_title}\\n*Scorecards Submitted:* {scorecards_submitted}\\n*Latest Recommendation:* {latest_recommendation} (by {submitted_by})\\n\\nAll feedback is in. Ready for hiring decision in Greenhouse.",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log feedback collection metrics",
        1720,
        "InterviewFeedback",
        '[["{candidate_name}","{job_title}","{interview_type}","{interviewer_name}","{scorecards_submitted}","{latest_recommendation}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "7", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "After a Greenhouse interview is completed, sends a scorecard reminder email to the interviewer. If no scorecard is submitted within 24 hours, nudges via Slack. Once feedback is collected, compiles all scorecards and notifies the recruiter that the candidate is ready for a hiring decision. Metrics are logged to Sheets.",
  },

  // ===================================================================
  // 187. NEW HIRE EQUIPMENT PROVISIONING — Pattern A (Welcome/Onboarding)
  // ===================================================================
  "New Hire Equipment Provisioning": {
    nodes: [
      trigger("BambooHR: Start Date Minus 7 Days", "webhook_app", {
        source: "BambooHR",
        event: "employee.start_approaching",
        howItStarts:
          "Fires 7 days before a new hire's start date to initiate equipment procurement and IT provisioning",
        setup:
          "In BambooHR > Settings > Webhooks, configure a webhook that fires when an employee's start date is 7 days away. Include employee_id, name, email, department, job_title, location (office/remote), and manager_name.",
        inputFields: [],
      }),

      n(
        "2",
        "runCode",
        "Determine equipment needs based on role and location",
        280,
        {
          codeDetails: {
            code: "const dept = '{department}'; const location = '{work_location}'; const role = '{job_title}'; const items = ['Laptop', 'Monitor', 'Keyboard & Mouse', 'Headset']; if (dept === 'Engineering') items.push('External SSD', 'USB Hub', 'Second Monitor'); if (dept === 'Design') items.push('Drawing Tablet', 'Color-calibrated Monitor'); if (location === 'remote') items.push('Shipping Label', 'Home Office Stipend'); return { equipment_list: items.join(', '), item_count: items.length, is_remote: location === 'remote', provisioning_priority: dept === 'Engineering' ? 'high' : 'standard' };",
            variables: [
              {
                name: "equipment_list",
                customName: "equipment_list",
                type: "string",
              },
              { name: "item_count", customName: "item_count", type: "number" },
              { name: "is_remote", customName: "is_remote", type: "boolean" },
              {
                name: "provisioning_priority",
                customName: "provisioning_priority",
                type: "string",
              },
            ],
          },
        },
      ),

      n("3", "hitApi", "Asana: Create provisioning tickets for IT", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"Equipment Provisioning: {employee_name} ({job_title})","projects":["{it_provisioning_project_id}"],"notes":"Start Date: {start_date}\\nDepartment: {department}\\nLocation: {work_location}\\nPriority: {provisioning_priority}\\n\\nEquipment Needed:\\n{equipment_list}","assignee":"{it_team_assignee}","due_on":"{provision_due_date}"}',
            },
          ],
          variables: [
            {
              name: "data.gid",
              customName: "provision_task_id",
              type: "string",
            },
            {
              name: "data.name",
              customName: "provision_task_name",
              type: "string",
            },
          ],
        },
      }),

      slackNode(
        "4",
        "Slack: Notify IT team of upcoming provisioning",
        760,
        "NEW HIRE PROVISIONING — 7 DAYS OUT\\n*Employee:* {employee_name} ({job_title})\\n*Start Date:* {start_date}\\n*Location:* {work_location} | *Priority:* {provisioning_priority}\\n*Equipment ({item_count} items):* {equipment_list}\\n\\nAsana task created. Please begin procurement and setup.",
      ),

      emailNode(
        "5",
        "Email: Send prep instructions to new hire",
        1000,
        "{employee_email}",
        "Getting ready for your first day, {employee_name}!",
        "Hi {employee_name},\n\nYour start date is just one week away and we are getting everything ready for you!\n\nHere is what we are preparing:\n- {equipment_list}\n\nWhat you can do now:\n1. Verify your shipping address (for remote employees) or confirm your office location\n2. Review the pre-boarding documents sent previously\n3. Prepare any questions for your first day\n\nYour manager {manager_name} and the IT team will ensure everything is set up and ready to go on day one.\n\nWe cannot wait to have you on the team!\n\nBest,\nThe People Team",
      ),

      n(
        "6",
        "delayAction",
        "Wait until start date to verify completion",
        1240,
        {
          delayDetails: { duration: 7, unit: "days" },
        },
      ),

      n("7", "condition", "Has all equipment been provisioned?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "all_provisioned", operator: "equals", value: "true" },
          ],
        },
      }),

      sheetsNode(
        "8",
        "Google Sheets: Log provisioning status",
        1720,
        "EquipmentProvisioning",
        '[["{employee_name}","{job_title}","{department}","{work_location}","{item_count}","{equipment_list}","{provisioning_priority}","{provision_task_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Triggered 7 days before a new hire's start date via BambooHR. Determines role-specific equipment needs, creates Asana provisioning tickets for IT, notifies the IT team via Slack, sends prep instructions to the new hire, and verifies completion on the start date. All provisioning data is logged to Sheets.",
  },

  // ===================================================================
  // 188. NEW MANAGER ONBOARDING PACK — Pattern A (Welcome/Onboarding)
  // ===================================================================
  "New Manager Onboarding Pack": {
    nodes: [
      trigger("BambooHR: Employee Promoted to Manager", "webhook_app", {
        source: "BambooHR",
        event: "employee.promotion",
        howItStarts:
          "Fires when an employee is promoted to a management role in BambooHR, initiating the new manager onboarding and development program",
        setup:
          "In BambooHR > Settings > Webhooks, configure a webhook for title/role changes that include 'Manager', 'Director', 'Lead', or 'Head' in the new title. Include employee_id, name, email, new_title, department, and previous_title.",
        inputFields: [],
      }),

      n("2", "runCode", "Generate manager onboarding resources package", 280, {
        codeDetails: {
          code: "const resources = ['Manager Handbook PDF', '1-on-1 Meeting Templates', 'Performance Review Guide', 'Hiring Process Overview', 'Budget Management Basics', 'Conflict Resolution Framework']; const learningPath = ['New Manager Orientation (Week 1)', 'HR Policies for Managers (Week 2)', 'Performance Management Workshop (Week 3)', 'Leadership Communication (Week 4)']; return { resource_list: resources.join('; '), learning_path: learningPath.join('; '), resource_count: resources.length, path_duration: '4 weeks' };",
          variables: [
            {
              name: "resource_list",
              customName: "resource_list",
              type: "string",
            },
            {
              name: "learning_path",
              customName: "learning_path",
              type: "string",
            },
            {
              name: "resource_count",
              customName: "resource_count",
              type: "number",
            },
            {
              name: "path_duration",
              customName: "path_duration",
              type: "string",
            },
          ],
        },
      }),

      emailNode(
        "3",
        "Email: Send manager toolkit and resources",
        520,
        "{manager_email}",
        "Congratulations on your promotion to {new_title}, {employee_name}!",
        "Dear {employee_name},\n\nCongratulations on your promotion to {new_title}! This is a well-deserved recognition of your contributions.\n\nAs you transition into management, we have put together a comprehensive toolkit to support you:\n\nResources ({resource_count} items):\n{resource_list}\n\nLearning Path ({path_duration}):\n{learning_path}\n\nKey things to know as a new manager:\n1. You now have access to BambooHR manager views for your team\n2. Schedule 1-on-1s with each direct report within your first week\n3. Review your team's performance history and goals\n4. Connect with your HR Business Partner for any questions\n\nWe are here to support your success!\n\nBest,\nThe People & Development Team",
      ),

      n("4", "hitApi", "Asana: Create manager learning tasks", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://app.asana.com/api/1.0/tasks",
          headers: [
            { key: "Authorization", value: "Bearer {asana_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "data",
              value:
                '{"name":"New Manager Onboarding: {employee_name}","projects":["{manager_development_project_id}"],"notes":"Role: {new_title}\\nDepartment: {department}\\n\\nLearning Path:\\n{learning_path}\\n\\nResources:\\n{resource_list}","assignee":"{employee_asana_id}"}',
            },
          ],
          variables: [
            {
              name: "data.gid",
              customName: "learning_task_id",
              type: "string",
            },
            {
              name: "data.name",
              customName: "learning_task_name",
              type: "string",
            },
          ],
        },
      }),

      slackNode(
        "5",
        "Slack: Add to #managers and welcome",
        1000,
        "Welcome to the managers community, *{employee_name}*!\\n*New Role:* {new_title} | *Department:* {department}\\n*Previous Role:* {previous_title}\\n\\nA learning path and resource kit have been shared. Feel free to reach out to fellow managers for advice and support!",
      ),

      n("6", "delayAction", "Wait 30 days for learning path check-in", 1240, {
        delayDetails: { duration: 30, unit: "days" },
      }),

      emailNode(
        "7",
        "Email: 30-day manager check-in",
        1480,
        "{manager_email}",
        "30-Day Check-In: How is your management journey going, {employee_name}?",
        "Hi {employee_name},\n\nIt has been 30 days since your promotion to {new_title}. We wanted to check in on how things are going.\n\nQuick self-assessment:\n- Have you completed the learning path modules?\n- Have you established regular 1-on-1s with your team?\n- Are there any challenges you need support with?\n\nPlease reply to this email or schedule time with your HRBP to discuss. We also recommend connecting with a management mentor from our mentor matching program.\n\nYou are doing great!\n\nBest,\nThe People & Development Team",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log manager onboarding progress",
        1720,
        "ManagerOnboarding",
        '[["{employee_name}","{new_title}","{previous_title}","{department}","{resource_count}","{path_duration}","{learning_task_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "When an employee is promoted to a management role in BambooHR, generates a manager resource package, sends the toolkit via email, creates Asana learning tasks, welcomes them in the #managers Slack channel, then sends a 30-day check-in email. Progress is tracked in Sheets.",
  },

  // ===================================================================
  // 189. 30/60/90-DAY CHECK-IN WORKFLOW — Pattern I (Content/Event Nurture)
  // ===================================================================
  "30/60/90-Day Check-In Workflow": {
    nodes: [
      trigger("BambooHR: New Hire 30-Day Milestone", "webhook_app", {
        source: "BambooHR",
        event: "employee.milestone",
        howItStarts:
          "Fires when a new hire reaches their 30-day employment milestone in BambooHR, triggering the structured check-in survey",
        setup:
          "In BambooHR > Settings > Webhooks, configure a webhook for employment milestones (30/60/90 days). Include employee_id, name, email, hire_date, department, manager_name, and milestone_days.",
        inputFields: [],
      }),

      emailNode(
        "2",
        "Email: Send check-in survey to new hire",
        280,
        "{employee_email}",
        "Your {milestone_days}-Day Check-In — We want to hear from you, {employee_name}",
        "Hi {employee_name},\n\nYou have been with us for {milestone_days} days! We want to hear how things are going.\n\nPlease take 5 minutes to complete this confidential check-in survey:\n\n{survey_link}\n\nYour feedback helps us:\n- Ensure you have everything you need to succeed\n- Identify any areas where we can improve the onboarding experience\n- Understand your early impressions of the team and culture\n\nYour responses are shared only with HR and your manager (unless you request otherwise).\n\nThank you for your time!\n\nBest,\nThe People Team",
      ),

      n("3", "delayAction", "Wait for survey response window", 520, {
        delayDetails: { duration: 5, unit: "days" },
      }),

      aiNode(
        "4",
        "OpenAI: Analyze check-in survey response",
        760,
        "You are an employee experience analyst. Analyze the new hire check-in survey response and identify satisfaction levels, concerns, and areas where the onboarding experience could be improved. Flag any responses that indicate risk of early attrition.",
        "Analyze this {milestone_days}-day check-in for {employee_name} in {department}: {{{survey_response}}}. Return JSON with: overall_satisfaction (1-10), concerns_flagged (boolean), concern_details (string or empty), onboarding_rating (string: excellent/good/needs_improvement/poor), attrition_risk (low/medium/high), recommended_follow_up (string).",
        [
          {
            field: "overall_satisfaction",
            as: "overall_satisfaction",
            type: "number",
          },
          {
            field: "concerns_flagged",
            as: "concerns_flagged",
            type: "boolean",
          },
          { field: "concern_details", as: "concern_details", type: "string" },
          {
            field: "onboarding_rating",
            as: "onboarding_rating",
            type: "string",
          },
          { field: "attrition_risk", as: "attrition_risk", type: "string" },
          {
            field: "recommended_follow_up",
            as: "recommended_follow_up",
            type: "string",
          },
        ],
      ),

      n("5", "condition", "Were concerns flagged in the check-in?", 1000, {
        conditionDetails: {
          conditions: [
            { field: "concerns_flagged", operator: "equals", value: "true" },
          ],
        },
      }),

      slackNode(
        "6",
        "Slack: Alert manager and HRBP of concerns",
        1240,
        "NEW HIRE CHECK-IN ALERT\\n*Employee:* {employee_name} ({milestone_days}-day check-in)\\n*Department:* {department} | *Manager:* {manager_name}\\n*Satisfaction:* {overall_satisfaction}/10 | *Rating:* {onboarding_rating}\\n*Attrition Risk:* {attrition_risk}\\n*Concerns:* {concern_details}\\n*Recommended Action:* {recommended_follow_up}",
      ),

      n(
        "7",
        "hitApi",
        "BambooHR: Log check-in notes on employee record",
        1480,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://api.bamboohr.com/api/gateway.php/{company_domain}/v1/employees/{employee_id}/notes",
            headers: [
              { key: "Authorization", value: "Basic {bamboohr_api_key}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "note",
                value:
                  "{milestone_days}-day check-in: Satisfaction {overall_satisfaction}/10. Rating: {onboarding_rating}. Risk: {attrition_risk}. Concerns: {concern_details}. Follow-up: {recommended_follow_up}",
              },
            ],
            variables: [
              { name: "id", customName: "checkin_note_id", type: "string" },
            ],
          },
        },
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log check-in data for trend analysis",
        1720,
        "CheckIns",
        '[["{employee_name}","{department}","{milestone_days}","{overall_satisfaction}","{onboarding_rating}","{attrition_risk}","{concerns_flagged}","{recommended_follow_up}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6", "output_1"),
      e("5", "7", "output_2"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "At the 30/60/90-day milestone, sends a check-in survey to the new hire via email. After a response window, AI analyzes the response for satisfaction levels and attrition risk. Concerns trigger Slack alerts to the manager and HRBP. Results are logged in BambooHR as employee notes and tracked in Sheets for onboarding trend analysis.",
  },

  // ===================================================================
  // 190. EXIT INTERVIEW ANALYSIS — Pattern I (Content/Event Nurture)
  // ===================================================================
  "Exit Interview Analysis": {
    nodes: [
      trigger("BambooHR: Employee Resignation Recorded", "webhook_app", {
        source: "BambooHR",
        event: "employee.resignation",
        howItStarts:
          "Fires when an employee resignation is recorded in BambooHR, triggering the exit interview scheduling and analysis workflow",
        setup:
          "In BambooHR > Settings > Webhooks, create a webhook for resignation events. Include employee_id, name, email, department, manager_name, last_day, and tenure_years in the payload.",
        inputFields: [],
      }),

      n("2", "hitApi", "Calendly: Schedule exit interview", 280, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.calendly.com/scheduling_links",
          headers: [
            { key: "Authorization", value: "Bearer {calendly_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "max_event_count", value: "1" },
            { key: "owner", value: "{hr_exit_interview_event_type}" },
            { key: "owner_type", value: "EventType" },
          ],
          variables: [
            {
              name: "resource.booking_url",
              customName: "exit_interview_url",
              type: "string",
            },
            {
              name: "resource.owner",
              customName: "interview_owner",
              type: "string",
            },
          ],
        },
      }),

      n("3", "delayAction", "Wait until after exit interview", 520, {
        delayDetails: { duration: 10, unit: "days" },
      }),

      aiNode(
        "4",
        "OpenAI: Analyze exit interview transcript",
        760,
        "You are an HR analytics specialist. Analyze the exit interview transcript and extract key themes, reasons for departure, and actionable insights. Categorize the departure reason and assess if it was preventable. Anonymize all sensitive details.",
        "Analyze exit interview for departing employee from {department} department, tenure {tenure_years} years: {{{exit_interview_notes}}}. Return JSON with: primary_reason (compensation/growth/management/culture/personal/opportunity), secondary_reasons (array), was_preventable (boolean), key_themes (array of strings), anonymized_insights (string without identifying info), sentiment_toward_company (positive/neutral/negative), improvement_suggestions (array).",
        [
          { field: "primary_reason", as: "primary_reason", type: "string" },
          {
            field: "secondary_reasons",
            as: "secondary_reasons",
            type: "string",
          },
          { field: "was_preventable", as: "was_preventable", type: "boolean" },
          { field: "key_themes", as: "key_themes", type: "string" },
          {
            field: "anonymized_insights",
            as: "anonymized_insights",
            type: "string",
          },
          {
            field: "sentiment_toward_company",
            as: "sentiment_toward_company",
            type: "string",
          },
        ],
      ),

      n("5", "runCode", "Categorize departure themes and flag patterns", 1000, {
        codeDetails: {
          code: "const preventable = was_preventable === 'true' || was_preventable === true; const reason = primary_reason; const isManagement = reason === 'management'; return { departure_category: reason, preventable_flag: preventable, management_issue: isManagement, theme_summary: key_themes, needs_leadership_review: preventable && (isManagement || reason === 'culture') };",
          variables: [
            {
              name: "departure_category",
              customName: "departure_category",
              type: "string",
            },
            {
              name: "preventable_flag",
              customName: "preventable_flag",
              type: "boolean",
            },
            {
              name: "needs_leadership_review",
              customName: "needs_leadership_review",
              type: "boolean",
            },
            {
              name: "theme_summary",
              customName: "theme_summary",
              type: "string",
            },
          ],
        },
      }),

      slackNode(
        "6",
        "Slack: Post anonymized exit insights to HR",
        1240,
        "EXIT INTERVIEW ANALYSIS\\n*Department:* {department} | *Tenure:* {tenure_years} years\\n*Primary Reason:* {departure_category}\\n*Preventable:* {preventable_flag}\\n*Sentiment:* {sentiment_toward_company}\\n*Key Themes:* {theme_summary}\\n*Anonymized Insights:* {anonymized_insights}",
      ),

      sheetsNode(
        "7",
        "Google Sheets: Log exit data for trend reporting",
        1480,
        "ExitInterviews",
        '[["{department}","{tenure_years}","{departure_category}","{secondary_reasons}","{preventable_flag}","{sentiment_toward_company}","{theme_summary}"]]',
      ),

      emailNode(
        "8",
        "Email: Send quarterly exit trend summary",
        1720,
        "{hr_leadership_email}",
        "Exit Interview Insights: {department} Department — {departure_category}",
        "An exit interview has been analyzed for a departing team member.\n\nDepartment: {department}\nTenure: {tenure_years} years\nPrimary Departure Reason: {departure_category}\nWas Preventable: {preventable_flag}\nSentiment: {sentiment_toward_company}\n\nKey Themes:\n{theme_summary}\n\nAnonymized Insights:\n{anonymized_insights}\n\nThis data has been added to the exit trend dashboard. If patterns emerge, we recommend discussing with department leadership.\n\nBest,\nPeople Analytics",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "When a resignation is recorded in BambooHR, schedules an exit interview via Calendly, then after the interview period uses AI to analyze the transcript. Categorizes departure reasons, flags preventable losses, posts anonymized insights to Slack for HR, logs data to Sheets for trend tracking, and emails a summary to HR leadership.",
  },

  // ===================================================================
  // 191. EXPENSE FORM TO APPROVAL TO REIMBURSEMENT — Pattern K
  // ===================================================================
  "Expense Form to Approval to Reimbursement": {
    nodes: [
      trigger("Webhook: Expense Report Submitted", "webhook_form", {
        source: "Internal",
        event: "expense.submitted",
        howItStarts:
          "Fires when an employee submits an expense report through the company expense portal or form",
        setup:
          "Configure the expense submission form to fire a webhook with employee_name, employee_email, expense_items (array), total_amount, category, receipt_urls, and business_justification.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract and validate expense line items",
        280,
        "You are an expense policy compliance analyst. Parse the expense submission and validate each line item against company policy. Flag any items that may violate spending policies (meals over $75, travel over $500 without pre-approval, entertainment expenses, etc.).",
        "Validate this expense report: {{{1.body}}}. Return JSON with: employee_name, total_amount (number), item_count (number), category, has_policy_violation (boolean), violation_details (string or empty), receipts_attached (boolean), expense_summary (brief description).",
        [
          { field: "employee_name", as: "employee_name", type: "string" },
          { field: "total_amount", as: "total_amount", type: "number" },
          { field: "item_count", as: "item_count", type: "number" },
          { field: "category", as: "expense_category", type: "string" },
          {
            field: "has_policy_violation",
            as: "has_policy_violation",
            type: "boolean",
          },
          {
            field: "violation_details",
            as: "violation_details",
            type: "string",
          },
          { field: "expense_summary", as: "expense_summary", type: "string" },
        ],
      ),

      n("3", "runCode", "Validate against expense policy limits", 520, {
        codeDetails: {
          code: "const amount = Number(total_amount); const violation = has_policy_violation === 'true' || has_policy_violation === true; return { needs_manager_approval: amount > 500 || violation, approval_reason: amount > 500 ? 'Over $500 threshold' : violation ? 'Policy concern: ' + violation_details : 'Within limits', auto_approved: amount <= 500 && !violation };",
          variables: [
            {
              name: "needs_manager_approval",
              customName: "needs_manager_approval",
              type: "boolean",
            },
            {
              name: "approval_reason",
              customName: "approval_reason",
              type: "string",
            },
            {
              name: "auto_approved",
              customName: "auto_approved",
              type: "boolean",
            },
          ],
        },
      }),

      n(
        "4",
        "condition",
        "Does expense exceed $500 or have policy concerns?",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                field: "needs_manager_approval",
                operator: "equals",
                value: "true",
              },
            ],
          },
        },
      ),

      n("5", "approvalGate", "Manager: Approve expense report", 1000, {
        approvalDetails: {
          approvers: ["direct_manager"],
          message:
            "Expense Report for Approval:\n\nEmployee: {employee_name}\nAmount: ${total_amount} ({item_count} items)\nCategory: {expense_category}\nSummary: {expense_summary}\nApproval Reason: {approval_reason}\n\nPlease approve or reject with comments.",
        },
      }),

      n(
        "6",
        "hitApi",
        "QuickBooks: Create expense entry for reimbursement",
        1240,
        {
          hitApiDetails: {
            method: "POST",
            url: "https://quickbooks.api.intuit.com/v3/company/{company_id}/purchase",
            headers: [
              { key: "Authorization", value: "Bearer {quickbooks_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              { key: "PaymentType", value: "Cash" },
              { key: "TotalAmt", value: "{total_amount}" },
              {
                key: "Line",
                value:
                  '[{"Amount":"{total_amount}","DetailType":"AccountBasedExpenseLineDetail","AccountBasedExpenseLineDetail":{"AccountRef":{"value":"{expense_account_id}"}}}]',
              },
              { key: "EntityRef", value: '{"value":"{employee_vendor_id}"}' },
            ],
            variables: [
              { name: "Id", customName: "qb_expense_id", type: "string" },
              {
                name: "TotalAmt",
                customName: "recorded_amount",
                type: "number",
              },
            ],
          },
        },
      ),

      emailNode(
        "7",
        "Email: Send approval and reimbursement confirmation",
        1480,
        "{employee_email}",
        "Expense Report Approved: ${total_amount} — Reimbursement Processing",
        "Hi {employee_name},\n\nYour expense report has been approved and is being processed for reimbursement.\n\nDetails:\n- Amount: ${total_amount}\n- Category: {expense_category}\n- Items: {item_count}\n- Summary: {expense_summary}\n\nReimbursement will be included in your next payroll cycle (typically within 5-7 business days).\n\nQuickBooks Reference: {qb_expense_id}\n\nThank you,\nFinance Team",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log expense for reporting",
        1720,
        "Expenses",
        '[["{employee_name}","{total_amount}","{expense_category}","{item_count}","{expense_summary}","{approval_reason}","{qb_expense_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Processes employee expense submissions through AI validation against company policy. Expenses over $500 or with policy violations route through manager approval, while compliant expenses are auto-approved. Approved expenses are recorded in QuickBooks for reimbursement, employees receive confirmation, and all data is logged to Sheets.",
  },

  // ===================================================================
  // 192. INVOICE RECEIVED TO APPROVAL WORKFLOW — Pattern K
  // ===================================================================
  "Invoice Received to Approval Workflow": {
    nodes: [
      trigger("Email: Invoice Received in AP Inbox", "email", {
        source: "Email",
        event: "email.received",
        howItStarts:
          "Fires when a vendor invoice email arrives at the accounts payable inbox (invoices@company.com), triggering extraction and approval routing",
        setup:
          "Configure the email trigger to monitor the invoices@company.com inbox. Forward all vendor invoice emails to this inbox. The workflow extracts details from the email body and attachments.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract invoice details from email",
        280,
        "You are an accounts payable processor. Extract all invoice details from the vendor email including invoice number, vendor name, amount, line items, payment terms, and due date. Match against any referenced purchase order numbers.",
        "Extract invoice details from this email: {{{1.body}}}. Return JSON with: vendor_name, invoice_number, invoice_amount (number), due_date, payment_terms (net 30/net 45/etc), po_number (string or empty), line_item_count (number), has_po_match (boolean), currency (USD/EUR/etc).",
        [
          { field: "vendor_name", as: "vendor_name", type: "string" },
          { field: "invoice_number", as: "invoice_number", type: "string" },
          { field: "invoice_amount", as: "invoice_amount", type: "number" },
          { field: "due_date", as: "due_date", type: "string" },
          { field: "payment_terms", as: "payment_terms", type: "string" },
          { field: "po_number", as: "po_number", type: "string" },
          { field: "has_po_match", as: "has_po_match", type: "boolean" },
        ],
      ),

      n("3", "runCode", "Match invoice to purchase order", 520, {
        codeDetails: {
          code: "const hasMatch = has_po_match === 'true' || has_po_match === true; const amount = Number(invoice_amount); return { po_matched: hasMatch, needs_dept_approval: !hasMatch || amount > 10000, approval_route: amount > 10000 ? 'department_head_and_finance' : hasMatch ? 'auto_approved' : 'department_head', invoice_ref: invoice_number + ' / ' + vendor_name + ' / $' + amount };",
          variables: [
            { name: "po_matched", customName: "po_matched", type: "boolean" },
            {
              name: "needs_dept_approval",
              customName: "needs_dept_approval",
              type: "boolean",
            },
            {
              name: "approval_route",
              customName: "approval_route",
              type: "string",
            },
            { name: "invoice_ref", customName: "invoice_ref", type: "string" },
          ],
        },
      }),

      n("4", "condition", "Does invoice match a purchase order?", 760, {
        conditionDetails: {
          conditions: [
            { field: "po_matched", operator: "equals", value: "true" },
          ],
        },
      }),

      n(
        "5",
        "approvalGate",
        "Department Head: Approve unmatched invoice",
        1000,
        {
          approvalDetails: {
            approvers: ["department_head"],
            message:
              "Invoice Pending Approval (No PO Match):\n\nVendor: {vendor_name}\nInvoice #: {invoice_number}\nAmount: ${invoice_amount}\nDue Date: {due_date}\nTerms: {payment_terms}\nPO Number: {po_number}\nRoute: {approval_route}\n\nPlease approve or reject with comments.",
          },
        },
      ),

      n("6", "hitApi", "QuickBooks: Create bill for approved invoice", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://quickbooks.api.intuit.com/v3/company/{company_id}/bill",
          headers: [
            { key: "Authorization", value: "Bearer {quickbooks_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "VendorRef", value: '{"value":"{vendor_qb_id}"}' },
            { key: "TotalAmt", value: "{invoice_amount}" },
            { key: "DueDate", value: "{due_date}" },
            { key: "DocNumber", value: "{invoice_number}" },
            {
              key: "Line",
              value:
                '[{"Amount":"{invoice_amount}","DetailType":"AccountBasedExpenseLineDetail","AccountBasedExpenseLineDetail":{"AccountRef":{"value":"{ap_account_id}"}}}]',
            },
          ],
          variables: [
            { name: "Id", customName: "qb_bill_id", type: "string" },
            { name: "Balance", customName: "bill_balance", type: "number" },
          ],
        },
      }),

      emailNode(
        "7",
        "Email: Send processing confirmation",
        1480,
        "{ap_team_email}",
        "Invoice Processed: {invoice_ref}",
        "The following invoice has been processed and entered into QuickBooks.\n\nVendor: {vendor_name}\nInvoice #: {invoice_number}\nAmount: ${invoice_amount}\nDue Date: {due_date}\nPayment Terms: {payment_terms}\nPO Match: {po_matched}\nQuickBooks Bill ID: {qb_bill_id}\n\nThe bill is queued for payment per the vendor's terms.\n\nBest,\nAP Automation",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log invoice processing",
        1720,
        "APInvoices",
        '[["{vendor_name}","{invoice_number}","{invoice_amount}","{due_date}","{payment_terms}","{po_matched}","{approval_route}","{qb_bill_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "6", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Monitors the AP inbox for vendor invoices, uses AI to extract invoice details and match to purchase orders. PO-matched invoices auto-process into QuickBooks, while unmatched invoices route through department head approval. All bills are created in QuickBooks and logged to Sheets for AP tracking.",
  },

  // ===================================================================
  // 193. CONTRACTOR INVOICE VALIDATION — Pattern K
  // ===================================================================
  "Contractor Invoice Validation": {
    nodes: [
      trigger("Webhook: Contractor Invoice Received", "webhook_app", {
        source: "Internal",
        event: "contractor.invoice",
        howItStarts:
          "Fires when a contractor submits an invoice through the contractor payment portal for validation against their contract terms",
        setup:
          "Configure the contractor portal to fire a webhook on invoice submission. Include contractor_id, name, email, invoice_amount, hours, rate, project_id, deliverables, and contract_id in payload.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract invoice amounts and validate terms",
        280,
        "You are a contractor payment validation specialist. Parse the contractor invoice and compare claimed hours, rate, and deliverables against the contract terms. Identify any discrepancies or scope concerns.",
        "Validate this contractor invoice: {{{1.body}}}. Return JSON with: contractor_name, invoice_amount (number), claimed_hours (number), contracted_rate (number), calculated_amount (number, hours*rate), has_discrepancy (boolean), discrepancy_details (string or empty), project_name, deliverables_complete (boolean).",
        [
          { field: "contractor_name", as: "contractor_name", type: "string" },
          { field: "invoice_amount", as: "invoice_amount", type: "number" },
          { field: "claimed_hours", as: "claimed_hours", type: "number" },
          {
            field: "calculated_amount",
            as: "calculated_amount",
            type: "number",
          },
          { field: "has_discrepancy", as: "has_discrepancy", type: "boolean" },
          {
            field: "discrepancy_details",
            as: "discrepancy_details",
            type: "string",
          },
          { field: "project_name", as: "project_name", type: "string" },
        ],
      ),

      n("3", "runCode", "Validate against contract budget and scope", 520, {
        codeDetails: {
          code: "const amount = Number(invoice_amount); const calculated = Number(calculated_amount); const discrepancy = has_discrepancy === 'true' || has_discrepancy === true; const withinScope = !discrepancy && Math.abs(amount - calculated) < 1; return { is_valid: withinScope, within_scope: withinScope, needs_pm_approval: !withinScope || amount > 5000, validation_note: withinScope ? 'Invoice matches contract terms' : 'Discrepancy: ' + discrepancy_details };",
          variables: [
            { name: "is_valid", customName: "is_valid", type: "boolean" },
            {
              name: "within_scope",
              customName: "within_scope",
              type: "boolean",
            },
            {
              name: "needs_pm_approval",
              customName: "needs_pm_approval",
              type: "boolean",
            },
            {
              name: "validation_note",
              customName: "validation_note",
              type: "string",
            },
          ],
        },
      }),

      n("4", "condition", "Is invoice within scope and valid?", 760, {
        conditionDetails: {
          conditions: [
            { field: "within_scope", operator: "equals", value: "true" },
          ],
        },
      }),

      n(
        "5",
        "approvalGate",
        "Project Manager: Approve out-of-scope invoice",
        1000,
        {
          approvalDetails: {
            approvers: ["project_manager"],
            message:
              "Contractor Invoice Needs Review:\n\nContractor: {contractor_name}\nProject: {project_name}\nInvoiced: ${invoice_amount} | Calculated: ${calculated_amount}\nHours: {claimed_hours}\nValidation: {validation_note}\n\nPlease approve or reject with comments.",
          },
        },
      ),

      n("6", "hitApi", "QuickBooks: Queue contractor payment", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://quickbooks.api.intuit.com/v3/company/{company_id}/bill",
          headers: [
            { key: "Authorization", value: "Bearer {quickbooks_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "VendorRef", value: '{"value":"{contractor_qb_id}"}' },
            { key: "TotalAmt", value: "{invoice_amount}" },
            { key: "DocNumber", value: "{invoice_id}" },
            {
              key: "Line",
              value:
                '[{"Amount":"{invoice_amount}","DetailType":"AccountBasedExpenseLineDetail","AccountBasedExpenseLineDetail":{"AccountRef":{"value":"{contractor_expense_account}"}}}]',
            },
          ],
          variables: [
            { name: "Id", customName: "qb_payment_id", type: "string" },
            { name: "TotalAmt", customName: "queued_amount", type: "number" },
          ],
        },
      }),

      emailNode(
        "7",
        "Email: Send payment confirmation to contractor",
        1480,
        "{contractor_email}",
        "Invoice Approved: ${invoice_amount} for {project_name}",
        "Hi {contractor_name},\n\nYour invoice has been validated and approved for payment.\n\nDetails:\n- Project: {project_name}\n- Amount: ${invoice_amount}\n- Hours: {claimed_hours}\n- Validation: {validation_note}\n\nPayment has been queued and will be processed according to the standard payment schedule (typically within 10 business days).\n\nThank you for your excellent work!\n\nBest,\nAccounts Payable",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log contractor payment",
        1720,
        "ContractorPayments",
        '[["{contractor_name}","{project_name}","{invoice_amount}","{claimed_hours}","{within_scope}","{validation_note}","{qb_payment_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "6", "output_1"),
      e("4", "5", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Validates contractor invoices by extracting amounts via AI and comparing against contract terms. Valid invoices auto-process to QuickBooks, while discrepant or high-value invoices route through project manager approval. Contractors receive email confirmation and all payments are logged to Sheets.",
  },

  // ===================================================================
  // 194. QUOTE-TO-CASH DEAL DESK — Pattern K (Lead Scoring/Routing)
  // ===================================================================
  "Quote-to-Cash Deal Desk": {
    nodes: [
      trigger("HubSpot: Deal Closed Won", "webhook_app", {
        source: "HubSpot",
        event: "deal.closed_won",
        howItStarts:
          "Fires when a deal is marked as closed-won in HubSpot CRM, triggering the invoicing and revenue collection process",
        setup:
          "In HubSpot > Settings > Workflows > Webhooks, create a webhook that fires when deal stage changes to 'Closed Won'. Include deal_id, amount, contact_email, company_name, payment_terms, and line_items in payload.",
        inputFields: [],
      }),

      n("2", "runCode", "Generate invoice details from deal data", 280, {
        codeDetails: {
          code: "const amount = Number('{deal_amount}'); const hasCustomTerms = '{payment_terms}' !== 'standard' && '{payment_terms}' !== ''; return { invoice_amount: amount, has_custom_terms: hasCustomTerms, payment_schedule: hasCustomTerms ? '{payment_terms}' : 'Net 30', needs_deal_desk: hasCustomTerms || amount > 50000, deal_ref: '{deal_id}' + ' / ' + '{company_name}' + ' / $' + amount };",
          variables: [
            {
              name: "invoice_amount",
              customName: "invoice_amount",
              type: "number",
            },
            {
              name: "has_custom_terms",
              customName: "has_custom_terms",
              type: "boolean",
            },
            {
              name: "payment_schedule",
              customName: "payment_schedule",
              type: "string",
            },
            {
              name: "needs_deal_desk",
              customName: "needs_deal_desk",
              type: "boolean",
            },
            { name: "deal_ref", customName: "deal_ref", type: "string" },
          ],
        },
      }),

      n("3", "condition", "Does this deal need deal desk review?", 520, {
        conditionDetails: {
          conditions: [
            { field: "needs_deal_desk", operator: "equals", value: "true" },
          ],
        },
      }),

      n(
        "4",
        "approvalGate",
        "Deal Desk: Review custom terms before invoicing",
        760,
        {
          approvalDetails: {
            approvers: ["deal_desk"],
            message:
              "Deal Desk Review Required:\n\nDeal: {deal_ref}\nAmount: ${invoice_amount}\nPayment Terms: {payment_schedule}\nCustom Terms: {has_custom_terms}\n\nPlease review and approve for invoicing.",
          },
        },
      ),

      n("5", "hitApi", "Stripe: Create and send invoice", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.stripe.com/v1/invoices",
          headers: [
            { key: "Authorization", value: "Bearer {stripe_secret_key}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            { key: "customer", value: "{stripe_customer_id}" },
            { key: "collection_method", value: "send_invoice" },
            { key: "days_until_due", value: "30" },
            {
              key: "description",
              value: "Invoice for {company_name} — Deal {deal_id}",
            },
          ],
          variables: [
            { name: "id", customName: "stripe_invoice_id", type: "string" },
            {
              name: "hosted_invoice_url",
              customName: "invoice_url",
              type: "string",
            },
            { name: "status", customName: "invoice_status", type: "string" },
          ],
        },
      }),

      emailNode(
        "6",
        "Email: Send invoice to customer",
        1240,
        "{contact_email}",
        "Invoice for {company_name} — ${invoice_amount}",
        "Dear {contact_name},\n\nThank you for your business! Please find your invoice below.\n\nInvoice Details:\n- Amount: ${invoice_amount}\n- Payment Terms: {payment_schedule}\n- Invoice Link: {invoice_url}\n\nYou can pay securely online using the link above. We accept credit cards, ACH, and wire transfers.\n\nIf you have questions about this invoice, please contact our billing team.\n\nThank you,\nThe Finance Team",
      ),

      n("7", "hitApi", "HubSpot: Update deal with invoice details", 1480, {
        hitApiDetails: {
          method: "PATCH",
          url: "https://api.hubapi.com/crm/v3/objects/deals/{deal_id}",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"invoice_id":"{stripe_invoice_id}","invoice_status":"{invoice_status}","invoice_url":"{invoice_url}"}',
            },
          ],
          variables: [
            { name: "id", customName: "updated_deal_id", type: "string" },
          ],
        },
      }),

      sheetsNode(
        "8",
        "Google Sheets: Log deal-to-invoice record",
        1720,
        "QuoteToCash",
        '[["{company_name}","{deal_id}","{invoice_amount}","{payment_schedule}","{stripe_invoice_id}","{invoice_status}","{needs_deal_desk}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"),
      e("3", "5", "output_2"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "When a HubSpot deal closes as won, generates invoice details and routes custom-term or high-value deals through deal desk approval. Creates a Stripe invoice and sends it to the customer, updates HubSpot with invoice tracking details, and logs the full quote-to-cash flow in Sheets.",
  },

  // ===================================================================
  // 195. PAYMENT LINK ON DEAL CHANGE — Pattern F (Renewal)
  // ===================================================================
  "Payment Link on Deal Change": {
    nodes: [
      trigger("HubSpot: Deal Stage Changed to Payment", "webhook_app", {
        source: "HubSpot",
        event: "deal.stage_change",
        howItStarts:
          "Fires when a HubSpot deal moves to the payment stage, triggering automatic payment link generation via Stripe",
        setup:
          "In HubSpot > Settings > Workflows > Webhooks, create a webhook that fires when deal stage changes to 'Payment'. Include deal_id, amount, contact_email, contact_name, and company_name.",
        inputFields: [],
      }),

      n("2", "runCode", "Compute payment amount and generate metadata", 280, {
        codeDetails: {
          code: "const amount = Number('{deal_amount}'); const amountCents = Math.round(amount * 100); return { amount_cents: amountCents, display_amount: '$' + amount.toFixed(2), company: '{company_name}', payment_description: '{company_name} — Deal #{deal_id}' };",
          variables: [
            {
              name: "amount_cents",
              customName: "amount_cents",
              type: "number",
            },
            {
              name: "display_amount",
              customName: "display_amount",
              type: "string",
            },
            { name: "company", customName: "company", type: "string" },
            {
              name: "payment_description",
              customName: "payment_description",
              type: "string",
            },
          ],
        },
      }),

      n("3", "hitApi", "Stripe: Create payment link", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.stripe.com/v1/payment_links",
          headers: [
            { key: "Authorization", value: "Bearer {stripe_secret_key}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            { key: "line_items[0][price_data][currency]", value: "usd" },
            {
              key: "line_items[0][price_data][product_data][name]",
              value: "{payment_description}",
            },
            {
              key: "line_items[0][price_data][unit_amount]",
              value: "{amount_cents}",
            },
            { key: "line_items[0][quantity]", value: "1" },
          ],
          variables: [
            { name: "id", customName: "payment_link_id", type: "string" },
            { name: "url", customName: "payment_url", type: "string" },
          ],
        },
      }),

      emailNode(
        "4",
        "Email: Send payment link to customer",
        760,
        "{contact_email}",
        "Payment Link for {company} — {display_amount}",
        "Hi {contact_name},\n\nThank you for moving forward! Your payment link is ready.\n\nAmount: {display_amount}\nDescription: {payment_description}\n\nPay securely here: {payment_url}\n\nThis link accepts credit cards and bank payments. Payment is securely processed through Stripe.\n\nIf you have questions or need alternative payment arrangements, please reply to this email.\n\nBest,\nThe Sales Team",
      ),

      slackNode(
        "5",
        "Slack: Notify rep that payment link was sent",
        1000,
        "PAYMENT LINK SENT\\n*Company:* {company}\\n*Amount:* {display_amount}\\n*Contact:* {contact_name} ({contact_email})\\n*Payment Link:* {payment_url}\\n\\nMonitoring for payment completion.",
      ),

      n("6", "delayAction", "Wait 48 hours for payment", 1240, {
        delayDetails: { duration: 48, unit: "hours" },
      }),

      n("7", "condition", "Has payment been completed?", 1480, {
        conditionDetails: {
          conditions: [
            { field: "payment_status", operator: "equals", value: "paid" },
          ],
        },
      }),

      n(
        "8",
        "hitApi",
        "HubSpot: Update deal stage based on payment status",
        1720,
        {
          hitApiDetails: {
            method: "PATCH",
            url: "https://api.hubapi.com/crm/v3/objects/deals/{deal_id}",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"dealstage":"{next_stage}","payment_link_id":"{payment_link_id}","payment_url":"{payment_url}"}',
              },
            ],
            variables: [
              { name: "id", customName: "updated_deal_id", type: "string" },
              {
                name: "properties.dealstage",
                customName: "final_stage",
                type: "string",
              },
            ],
          },
        },
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "When a HubSpot deal moves to the payment stage, computes the amount, creates a Stripe payment link, emails it to the customer, and notifies the sales rep via Slack. After 48 hours, checks payment status and updates the HubSpot deal stage accordingly.",
  },

  // ===================================================================
  // 196. SUBSCRIPTION SWAP OR REFUND OPS — Pattern F (Renewal)
  // ===================================================================
  "Subscription Swap or Refund Ops": {
    nodes: [
      trigger("Webhook: Subscription Swap or Refund Request", "webhook_app", {
        source: "Internal",
        event: "subscription.change_request",
        howItStarts:
          "Fires when a customer submits a request to swap their subscription plan or request a refund through the support portal",
        setup:
          "Configure the support portal to fire a webhook on swap/refund requests. Include customer_email, subscription_id, request_type (swap/refund), current_plan, desired_plan, and reason in the payload.",
        inputFields: [],
      }),

      n("2", "hitApi", "Stripe: Fetch current subscription details", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.stripe.com/v1/subscriptions/{subscription_id}",
          headers: [
            { key: "Authorization", value: "Bearer {stripe_secret_key}" },
          ],
          variables: [
            { name: "id", customName: "sub_id", type: "string" },
            { name: "status", customName: "sub_status", type: "string" },
            {
              name: "current_period_end",
              customName: "period_end",
              type: "number",
            },
            {
              name: "items.data[0].price.unit_amount",
              customName: "current_price_cents",
              type: "number",
            },
            {
              name: "customer",
              customName: "stripe_customer_id",
              type: "string",
            },
          ],
        },
      }),

      n("3", "runCode", "Compute prorated refund or swap amount", 520, {
        codeDetails: {
          code: "const currentPrice = Number(current_price_cents) / 100; const periodEnd = Number(period_end); const now = Math.floor(Date.now() / 1000); const daysRemaining = Math.ceil((periodEnd - now) / 86400); const totalDays = 30; const prorated = Math.round((currentPrice * daysRemaining / totalDays) * 100) / 100; return { prorated_amount: prorated, days_remaining: daysRemaining, current_price: currentPrice, needs_finance_approval: prorated > 200, refund_summary: '$' + prorated + ' prorated (' + daysRemaining + ' days remaining)' };",
          variables: [
            {
              name: "prorated_amount",
              customName: "prorated_amount",
              type: "number",
            },
            {
              name: "days_remaining",
              customName: "days_remaining",
              type: "number",
            },
            {
              name: "needs_finance_approval",
              customName: "needs_finance_approval",
              type: "boolean",
            },
            {
              name: "refund_summary",
              customName: "refund_summary",
              type: "string",
            },
          ],
        },
      }),

      n(
        "4",
        "condition",
        "Does refund exceed $200 requiring finance approval?",
        760,
        {
          conditionDetails: {
            conditions: [
              {
                field: "needs_finance_approval",
                operator: "equals",
                value: "true",
              },
            ],
          },
        },
      ),

      n("5", "approvalGate", "Finance: Approve large refund or swap", 1000, {
        approvalDetails: {
          approvers: ["finance_team"],
          message:
            "Subscription Change Requires Finance Approval:\n\nCustomer: {customer_email}\nRequest Type: {request_type}\nCurrent Plan: {current_plan}\nProrated Amount: {refund_summary}\n\nPlease approve or reject.",
        },
      }),

      n("6", "hitApi", "Stripe: Execute subscription change or refund", 1240, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.stripe.com/v1/refunds",
          headers: [
            { key: "Authorization", value: "Bearer {stripe_secret_key}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          body: [
            { key: "payment_intent", value: "{latest_payment_intent}" },
            { key: "amount", value: "{prorated_amount_cents}" },
            { key: "reason", value: "requested_by_customer" },
          ],
          variables: [
            { name: "id", customName: "refund_id", type: "string" },
            { name: "status", customName: "refund_status", type: "string" },
            { name: "amount", customName: "refunded_amount", type: "number" },
          ],
        },
      }),

      emailNode(
        "7",
        "Email: Confirm subscription change to customer",
        1480,
        "{customer_email}",
        "Your Subscription Change Has Been Processed",
        "Hi,\n\nYour subscription {request_type} request has been processed successfully.\n\nDetails:\n- Request Type: {request_type}\n- Previous Plan: {current_plan}\n- Prorated Amount: {refund_summary}\n- Refund ID: {refund_id}\n- Status: {refund_status}\n\nIf you requested a swap, your new plan will take effect immediately. If you requested a refund, it will appear on your statement within 5-10 business days.\n\nQuestions? Reply to this email or contact our support team.\n\nBest,\nThe Billing Team",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log subscription change",
        1720,
        "SubscriptionChanges",
        '[["{customer_email}","{request_type}","{current_plan}","{prorated_amount}","{refund_id}","{refund_status}","{needs_finance_approval}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Processes subscription swap or refund requests by fetching Stripe subscription details, computing prorated amounts, and routing high-value refunds through finance approval. Executes the change in Stripe, confirms to the customer via email, and logs all subscription changes to Sheets.",
  },

  // ===================================================================
  // 197. FAILED PAYMENT RECOVERY — Pattern F (Renewal/Dunning)
  // ===================================================================
  "Failed Payment Recovery": {
    nodes: [
      trigger("Stripe: Payment Failed", "webhook_app", {
        source: "Stripe",
        event: "invoice.payment_failed",
        howItStarts:
          "Fires when a Stripe invoice payment fails, triggering the dunning recovery sequence to update payment methods and retry",
        setup:
          "In Stripe > Developers > Webhooks, create an endpoint for 'invoice.payment_failed' events. Include customer_id, invoice_id, amount_due, failure_code, and attempt_count.",
        inputFields: [],
      }),

      n(
        "2",
        "runCode",
        "Classify failure type and determine recovery strategy",
        280,
        {
          codeDetails: {
            code: "const code = '{failure_code}'; const amount = Number('{amount_due}') / 100; const attempt = Number('{attempt_count}'); const isCard = code === 'card_declined' || code === 'insufficient_funds' || code === 'expired_card'; const isAuth = code === 'authentication_required'; return { failure_type: isCard ? 'card_issue' : isAuth ? 'auth_required' : 'other', recovery_message: isCard ? 'Please update your payment method' : isAuth ? 'Your bank requires authentication' : 'A payment error occurred', display_amount: '$' + amount.toFixed(2), attempt_number: attempt, is_final_attempt: attempt >= 3 };",
            variables: [
              {
                name: "failure_type",
                customName: "failure_type",
                type: "string",
              },
              {
                name: "recovery_message",
                customName: "recovery_message",
                type: "string",
              },
              {
                name: "display_amount",
                customName: "display_amount",
                type: "string",
              },
              {
                name: "attempt_number",
                customName: "attempt_number",
                type: "number",
              },
              {
                name: "is_final_attempt",
                customName: "is_final_attempt",
                type: "boolean",
              },
            ],
          },
        },
      ),

      emailNode(
        "3",
        "Email: Request payment method update",
        520,
        "{customer_email}",
        "Action Required: Payment of {display_amount} Failed",
        "Hi,\n\nWe were unable to process your payment of {display_amount}.\n\nReason: {recovery_message}\nAttempt: {attempt_number}\n\nTo avoid service interruption, please update your payment method:\n\n{payment_update_url}\n\nIf you recently updated your card, it may take a few minutes to process. We will automatically retry your payment.\n\nNeed help? Our support team is here for you.\n\nBest,\nThe Billing Team",
      ),

      n("4", "delayAction", "Wait 3 days before retry", 760, {
        delayDetails: { duration: 3, unit: "days" },
      }),

      n("5", "hitApi", "Stripe: Retry failed invoice payment", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.stripe.com/v1/invoices/{invoice_id}/pay",
          headers: [
            { key: "Authorization", value: "Bearer {stripe_secret_key}" },
            { key: "Content-Type", value: "application/x-www-form-urlencoded" },
          ],
          variables: [
            { name: "id", customName: "retried_invoice_id", type: "string" },
            { name: "status", customName: "retry_status", type: "string" },
            { name: "paid", customName: "payment_successful", type: "boolean" },
          ],
        },
      }),

      n("6", "condition", "Is payment still failing after retry?", 1240, {
        conditionDetails: {
          conditions: [
            { field: "payment_successful", operator: "equals", value: "false" },
          ],
        },
      }),

      slackNode(
        "7",
        "Slack: Notify CS team of persistent payment failure",
        1480,
        "PAYMENT RECOVERY ALERT\\n*Customer:* {customer_email}\\n*Amount:* {display_amount}\\n*Failure Type:* {failure_type}\\n*Attempts:* {attempt_number}\\n*Retry Status:* {retry_status}\\n\\nPayment has failed after retry. Customer may need direct outreach to prevent churn.",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log payment recovery attempt",
        1720,
        "PaymentRecovery",
        '[["{customer_email}","{display_amount}","{failure_type}","{attempt_number}","{retry_status}","{payment_successful}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
    summary:
      "When a Stripe payment fails, classifies the failure type, emails the customer to update their payment method, waits 3 days, then retries the payment. Persistent failures trigger a Slack alert to the CS team for personal outreach. All recovery attempts are logged to Sheets for dunning analytics.",
  },

  // ===================================================================
  // 198. DONATION TRACKING INTO CRM — Pattern E (Post-Purchase)
  // ===================================================================
  "Donation Tracking into CRM": {
    nodes: [
      trigger("Stripe: Donation Payment Received", "webhook_app", {
        source: "Stripe",
        event: "charge.succeeded",
        howItStarts:
          "Fires when a donation payment is successfully processed through Stripe, triggering CRM recording and donor acknowledgment",
        setup:
          "In Stripe > Developers > Webhooks, create an endpoint for 'charge.succeeded' events filtered by donation-related metadata. Include charge_id, customer_email, amount, currency, and metadata (campaign_name, is_recurring).",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract donor details and donation context",
        280,
        "You are a nonprofit donation processor. Parse the Stripe charge webhook and extract donor details, donation amount, campaign attribution, and determine if this is a first-time or recurring donor.",
        "Process this donation payment: {{{1.body}}}. Return JSON with: donor_email, donor_name, donation_amount (number), campaign_name, is_recurring (boolean), is_first_donation (boolean), currency, donation_date, tax_deductible (boolean).",
        [
          { field: "donor_email", as: "donor_email", type: "string" },
          { field: "donor_name", as: "donor_name", type: "string" },
          { field: "donation_amount", as: "donation_amount", type: "number" },
          { field: "campaign_name", as: "campaign_name", type: "string" },
          { field: "is_recurring", as: "is_recurring", type: "boolean" },
          {
            field: "is_first_donation",
            as: "is_first_donation",
            type: "boolean",
          },
        ],
      ),

      n("3", "hitApi", "HubSpot: Create or update donor contact", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.hubapi.com/crm/v3/objects/contacts",
          headers: [
            { key: "Authorization", value: "Bearer {hubspot_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "properties",
              value:
                '{"email":"{donor_email}","firstname":"{donor_name}","donor_status":"active","last_donation_amount":"{donation_amount}","last_donation_campaign":"{campaign_name}","is_recurring_donor":"{is_recurring}"}',
            },
          ],
          variables: [
            { name: "id", customName: "hubspot_donor_id", type: "string" },
            {
              name: "properties.email",
              customName: "stored_email",
              type: "string",
            },
          ],
        },
      }),

      n("4", "runCode", "Tag donation campaign and compute donor level", 760, {
        codeDetails: {
          code: "const amount = Number(donation_amount); const level = amount >= 1000 ? 'Major Donor' : amount >= 250 ? 'Sustaining Donor' : amount >= 50 ? 'Supporting Donor' : 'Friend'; const recurring = is_recurring === 'true' || is_recurring === true; return { donor_level: level, campaign_tag: campaign_name, is_recurring_flag: recurring, receipt_amount: '$' + amount.toFixed(2) };",
          variables: [
            { name: "donor_level", customName: "donor_level", type: "string" },
            {
              name: "campaign_tag",
              customName: "campaign_tag",
              type: "string",
            },
            {
              name: "is_recurring_flag",
              customName: "is_recurring_flag",
              type: "boolean",
            },
            {
              name: "receipt_amount",
              customName: "receipt_amount",
              type: "string",
            },
          ],
        },
      }),

      emailNode(
        "5",
        "Email: Send thank-you receipt to donor",
        1000,
        "{donor_email}",
        "Thank You for Your Generous Donation of {receipt_amount}, {donor_name}!",
        "Dear {donor_name},\n\nThank you so much for your generous donation of {receipt_amount} to the {campaign_name} campaign!\n\nYour support makes a real difference. This donation is tax-deductible to the extent allowed by law.\n\nDonation Receipt:\n- Amount: {receipt_amount}\n- Campaign: {campaign_name}\n- Donor Level: {donor_level}\n- Date: Today\n\nPlease save this email for your tax records.\n\nWith gratitude,\nThe Development Team",
      ),

      n("6", "condition", "Is this a recurring donation?", 1240, {
        conditionDetails: {
          conditions: [
            { field: "is_recurring_flag", operator: "equals", value: "true" },
          ],
        },
      }),

      slackNode(
        "7",
        "Slack: Notify development team of recurring donor",
        1480,
        "NEW RECURRING DONOR\\n*Donor:* {donor_name} ({donor_email})\\n*Amount:* {receipt_amount}/month\\n*Campaign:* {campaign_name}\\n*Level:* {donor_level}\\n\\nRecurring donors are key to our sustainability. Consider a personal thank-you note.",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log donation record",
        1720,
        "Donations",
        '[["{donor_name}","{donor_email}","{donation_amount}","{campaign_name}","{donor_level}","{is_recurring_flag}","{hubspot_donor_id}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"),
      e("6", "8", "output_2"),
      e("7", "8"),
    ],
    summary:
      "When a Stripe donation succeeds, AI extracts donor details, creates or updates the donor in HubSpot, tags the campaign and donor level, and sends a tax-deductible thank-you receipt. Recurring donors trigger a Slack notification to the development team for personalized follow-up. All donations are logged to Sheets.",
  },

  // ===================================================================
  // 199. NONPROFIT CAMPAIGN CLOSE UPDATES — Pattern I (Content/Event)
  // ===================================================================
  "Nonprofit Campaign Close Updates": {
    nodes: [
      trigger("Schedule: Campaign End Date Reached", "schedule", {
        schedule: "0 9 * * *",
        howItStarts:
          "Runs daily at 9 AM to check if any fundraising campaigns have reached their close date, triggering final reporting and stakeholder updates",
        setup:
          "Configure a daily schedule at 9 AM. Ensure campaign end dates are tracked in a database or spreadsheet. Stripe API credentials must be configured for fetching campaign totals.",
        inputFields: [],
      }),

      n("2", "hitApi", "Stripe: Fetch campaign donation totals", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.stripe.com/v1/charges?limit=100&created[gte]={campaign_start_timestamp}&created[lte]={campaign_end_timestamp}",
          headers: [
            { key: "Authorization", value: "Bearer {stripe_secret_key}" },
          ],
          variables: [
            {
              name: "data.length",
              customName: "total_donations",
              type: "number",
            },
            { name: "data", customName: "donations_data", type: "string" },
          ],
        },
      }),

      n("3", "runCode", "Compute campaign totals vs goal", 520, {
        codeDetails: {
          code: "const donations = JSON.parse(donations_data || '[]'); const totalRaised = donations.reduce((sum, d) => sum + (d.amount / 100), 0); const goal = Number('{campaign_goal}'); const pctOfGoal = Math.round((totalRaised / goal) * 100); const exceeded = totalRaised >= goal; return { total_raised: totalRaised, pct_of_goal: pctOfGoal, goal_met: exceeded, donor_count: donations.length, display_total: '$' + totalRaised.toLocaleString(), display_goal: '$' + goal.toLocaleString() };",
          variables: [
            {
              name: "total_raised",
              customName: "total_raised",
              type: "number",
            },
            { name: "pct_of_goal", customName: "pct_of_goal", type: "number" },
            { name: "goal_met", customName: "goal_met", type: "boolean" },
            { name: "donor_count", customName: "donor_count", type: "number" },
            {
              name: "display_total",
              customName: "display_total",
              type: "string",
            },
            {
              name: "display_goal",
              customName: "display_goal",
              type: "string",
            },
          ],
        },
      }),

      aiNode(
        "4",
        "OpenAI: Generate campaign close report",
        760,
        "You are a nonprofit communications specialist. Generate a compelling campaign close report that highlights achievements, thanks donors, and provides key metrics. Adjust tone based on whether the goal was met or not.",
        "Generate a campaign close report for: campaign={campaign_name}, total_raised={display_total}, goal={display_goal}, pct_achieved={pct_of_goal}%, donors={donor_count}, goal_met={goal_met}. Return JSON with: report_summary (3-4 sentence narrative), headline (compelling one-liner), key_metrics (formatted string), thank_you_message (for donors), next_steps (what happens next).",
        [
          { field: "report_summary", as: "report_summary", type: "string" },
          { field: "headline", as: "headline", type: "string" },
          { field: "key_metrics", as: "key_metrics", type: "string" },
          {
            field: "thank_you_message",
            as: "thank_you_message",
            type: "string",
          },
          { field: "next_steps", as: "next_steps", type: "string" },
        ],
      ),

      emailNode(
        "5",
        "Email: Send campaign close report to board",
        1000,
        "{board_email}",
        "Campaign Results: {campaign_name} — {headline}",
        "Dear Board Members,\n\n{report_summary}\n\nKey Metrics:\n{key_metrics}\n\n- Total Raised: {display_total}\n- Goal: {display_goal}\n- Achievement: {pct_of_goal}%\n- Total Donors: {donor_count}\n\nNext Steps:\n{next_steps}\n\nThank you for your leadership and support in making this campaign possible.\n\nBest,\nThe Development Team",
      ),

      slackNode(
        "6",
        "Slack: Post final campaign numbers",
        1240,
        "CAMPAIGN CLOSED: *{campaign_name}*\\n\\n{headline}\\n\\n*{display_total}* raised of {display_goal} goal ({pct_of_goal}%)\\n*{donor_count}* total donors\\n\\n{report_summary}",
      ),

      n(
        "7",
        "hitApi",
        "HubSpot: Update campaign record with final results",
        1480,
        {
          hitApiDetails: {
            method: "PATCH",
            url: "https://api.hubapi.com/crm/v3/objects/deals/{campaign_deal_id}",
            headers: [
              { key: "Authorization", value: "Bearer {hubspot_token}" },
              { key: "Content-Type", value: "application/json" },
            ],
            body: [
              {
                key: "properties",
                value:
                  '{"campaign_status":"closed","total_raised":"{total_raised}","donor_count":"{donor_count}","pct_of_goal":"{pct_of_goal}"}',
              },
            ],
            variables: [
              { name: "id", customName: "updated_campaign_id", type: "string" },
            ],
          },
        },
      ),

      sheetsNode(
        "8",
        "Google Sheets: Archive campaign results",
        1720,
        "CampaignResults",
        '[["{campaign_name}","{display_total}","{display_goal}","{pct_of_goal}%","{donor_count}","{goal_met}","{headline}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Runs daily to check for ended fundraising campaigns. Fetches Stripe donation totals, computes performance against goals, and uses AI to generate a compelling close report. Results are emailed to the board, posted to Slack, recorded in HubSpot, and archived to Sheets for historical reporting.",
  },

  // ===================================================================
  // 200. PURCHASE REQUEST APPROVAL — Pattern K (Lead Scoring/Routing)
  // ===================================================================
  "Purchase Request Approval": {
    nodes: [
      trigger("Webhook: Purchase Request Submitted", "webhook_form", {
        source: "Internal",
        event: "purchase.requested",
        howItStarts:
          "Fires when an employee submits a purchase request through the internal procurement form for budget review and approval",
        setup:
          "Configure the procurement request form to fire a webhook. Include requester_name, requester_email, item_description, amount, category, business_justification, vendor, and urgency in payload.",
        inputFields: [],
      }),

      aiNode(
        "2",
        "OpenAI: Extract purchase details and assess justification",
        280,
        "You are a procurement analyst. Parse the purchase request and evaluate the business justification against common procurement policies. Assess whether the request is well-justified, has proper vendor selection rationale, and fits within typical spending patterns.",
        "Analyze this purchase request: {{{1.body}}}. Return JSON with: requester_name, item_description, amount (number), category, vendor, justification_quality (strong/adequate/weak), risk_flags (string or empty), budget_category (opex/capex), urgency_level (high/medium/low).",
        [
          { field: "requester_name", as: "requester_name", type: "string" },
          { field: "item_description", as: "item_description", type: "string" },
          { field: "amount", as: "request_amount", type: "number" },
          { field: "category", as: "category", type: "string" },
          { field: "vendor", as: "vendor", type: "string" },
          {
            field: "justification_quality",
            as: "justification_quality",
            type: "string",
          },
          { field: "budget_category", as: "budget_category", type: "string" },
          { field: "urgency_level", as: "urgency_level", type: "string" },
        ],
      ),

      n(
        "3",
        "runCode",
        "Check budget availability and determine approval route",
        520,
        {
          codeDetails: {
            code: "const amount = Number(request_amount); const highValue = amount > 1000; const needsFinance = amount > 5000; return { needs_approval: highValue, approval_level: needsFinance ? 'finance_and_manager' : highValue ? 'manager_only' : 'auto_approved', is_high_value: highValue, request_ref: 'PR-' + Date.now(), display_amount: '$' + amount.toFixed(2) };",
            variables: [
              {
                name: "needs_approval",
                customName: "needs_approval",
                type: "boolean",
              },
              {
                name: "approval_level",
                customName: "approval_level",
                type: "string",
              },
              {
                name: "is_high_value",
                customName: "is_high_value",
                type: "boolean",
              },
              {
                name: "request_ref",
                customName: "request_ref",
                type: "string",
              },
              {
                name: "display_amount",
                customName: "display_amount",
                type: "string",
              },
            ],
          },
        },
      ),

      n(
        "4",
        "condition",
        "Does purchase exceed $1000 requiring approval?",
        760,
        {
          conditionDetails: {
            conditions: [
              { field: "needs_approval", operator: "equals", value: "true" },
            ],
          },
        },
      ),

      n(
        "5",
        "approvalGate",
        "Manager + Finance: Approve purchase request",
        1000,
        {
          approvalDetails: {
            approvers: ["manager", "finance_reviewer"],
            message:
              "Purchase Request for Approval:\n\nRef: {request_ref}\nRequester: {requester_name}\nItem: {item_description}\nAmount: {display_amount}\nVendor: {vendor}\nCategory: {category} ({budget_category})\nJustification Quality: {justification_quality}\nUrgency: {urgency_level}\n\nPlease approve or reject with comments.",
          },
        },
      ),

      emailNode(
        "6",
        "Email: Send approval confirmation to requester",
        1240,
        "{requester_email}",
        "Purchase Request Approved: {item_description} — {display_amount}",
        "Hi {requester_name},\n\nYour purchase request has been approved!\n\nDetails:\n- Reference: {request_ref}\n- Item: {item_description}\n- Amount: {display_amount}\n- Vendor: {vendor}\n- Category: {category}\n- Approval Level: {approval_level}\n\nYou may now proceed with the purchase. Please submit the receipt to accounts payable once the purchase is complete.\n\nThank you,\nProcurement Team",
      ),

      slackNode(
        "7",
        "Slack: Notify procurement team",
        1480,
        "PURCHASE APPROVED\\n*Ref:* {request_ref}\\n*Requester:* {requester_name}\\n*Item:* {item_description}\\n*Amount:* {display_amount}\\n*Vendor:* {vendor}\\n*Approval:* {approval_level}\\n*Urgency:* {urgency_level}",
      ),

      sheetsNode(
        "8",
        "Google Sheets: Log purchase request",
        1720,
        "PurchaseRequests",
        '[["{request_ref}","{requester_name}","{item_description}","{request_amount}","{vendor}","{category}","{approval_level}","{justification_quality}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"),
      e("4", "6", "output_2"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
    summary:
      "Processes employee purchase requests through AI justification analysis and budget routing. Requests under $1000 are auto-approved, while higher amounts route through manager and/or finance approval gates. Approved purchases trigger email confirmation to the requester, Slack notification to procurement, and logging to Sheets.",
  },
};
